import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Tally-style global keyboard shortcuts for accounts pages.
 *
 *  F2  → focus search input on the page (any [data-search-target])
 *  F4  → Customer master
 *  F5  → reload current page
 *  F6  → Receipts (All dues, where you record payments)
 *  F8  → New sale (new invoice editor)
 *  F11 → Day Book
 *
 *  Path is automatically /admin or /employee/accounts based on the current URL.
 */
export default function useTallyShortcuts() {
    const nav = useNavigate();

    useEffect(() => {
        const root = window.location.pathname.startsWith("/admin")
            ? "/admin"
            : "/employee/accounts";

        const handler = (e) => {
            // Don't fire when user is typing in any editable element other than search
            const tag = (e.target?.tagName || "").toLowerCase();
            const isTyping = ["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable;
            const isSearch = e.target?.hasAttribute?.("data-search-target");

            // F2 — focus the first search input on the page
            if (e.key === "F2") {
                e.preventDefault();
                const el = document.querySelector("[data-search-target], input[placeholder*='Search']");
                if (el) el.focus();
                return;
            }

            // Other shortcuts: skip when typing
            if (isTyping && !isSearch) return;

            switch (e.key) {
                case "F4":
                    e.preventDefault();
                    nav(root === "/admin" ? "/admin/customers" : "/employee/accounts/customers");
                    break;
                case "F6":
                    e.preventDefault();
                    nav(root === "/admin" ? "/admin/dues" : "/employee/accounts/dues");
                    break;
                case "F8":
                    e.preventDefault();
                    nav(root === "/admin" ? "/admin/invoices/new" : "/employee/accounts/invoices/new");
                    break;
                case "F11":
                    e.preventDefault();
                    nav(root === "/admin" ? "/admin/daybook" : "/employee/accounts/daybook");
                    break;
                default:
                    break;
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [nav]);
}

export const SHORTCUT_HINTS = [
    { key: "F2",  label: "Search" },
    { key: "F4",  label: "Customer master" },
    { key: "F6",  label: "Receipts / Dues" },
    { key: "F8",  label: "New sale" },
    { key: "F11", label: "Day Book" },
];
